import { useState, useEffect } from "react";
import axios from "axios";
import { API, useAuth } from "@/App";
import { resolvePublicLink } from "@/lib/utils";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import { CalendarClock, Save, Trash2, Check, Link2, Copy, Bell, MessageCircle, Mail, Send } from "lucide-react";

const JOURS = [{ n: 1, l: "Lun" }, { n: 2, l: "Mar" }, { n: 3, l: "Mer" }, { n: 4, l: "Jeu" }, { n: 5, l: "Ven" }, { n: 6, l: "Sam" }, { n: 7, l: "Dim" }];
const STATUT_COLORS = { a_venir: "bg-blue-100 text-blue-800", confirme: "bg-green-100 text-green-800", annule: "bg-slate-100 text-slate-500" };
const STATUT_LABELS = { a_venir: "À venir", confirme: "Confirmé", annule: "Annulé" };

// Modèles SMS pour le lien RDV
const buildRdvSmsMessage = (typeLien, prenom, lien) => {
  const salut = prenom ? `Bonjour ${prenom},` : "Bonjour,";
  if (typeLien === "retrait") {
    return `${salut} choisissez ici le jour et l'heure pour retirer votre commande TopCocoon à notre dépôt-showroom d'Anderlecht : ${lien}\nVotre commande sera préparée avant votre arrivée.\nÀ bientôt,\nTopCocoon`;
  }
  return `${salut} choisissez ici le jour et l'heure de votre visite au showroom TopCocoon à Anderlecht : ${lien}\nVous pourrez découvrir et essayer nos chaises sans obligation d'achat.\nÀ bientôt,\nTopCocoon`;
};

const RendezVous = () => {
  const { token } = useAuth();
  const headers = { Authorization: `Bearer ${token}` };
  const [cfg, setCfg] = useState(null);
  const [params, setParams] = useState(null);
  const [rdvs, setRdvs] = useState([]);
  const [produits, setProduits] = useState([]);
  // Résout le lien public depuis Paramètres → Liens publics (fallback /prendre-rdv sur l'origine actuelle)
  const publicUrl = resolvePublicLink(params?.lien_rdv, "/prendre-rdv");

  // Dialog "Envoyer le lien par SMS"
  const [smsOpen, setSmsOpen] = useState(false);
  const [smsData, setSmsData] = useState({
    rdv_id: "",
    telephone: "",
    prenom: "",
    type_lien: "visite",
    message: "",
  });
  const [smsSending, setSmsSending] = useState(false);
  const [smsError, setSmsError] = useState("");

  const fetchAll = async () => {
    try {
      const [c, r, p, prod] = await Promise.all([
        axios.get(`${API}/rdv/config`, { headers }),
        axios.get(`${API}/rdv`, { headers }),
        axios.get(`${API}/parametres`, { headers }),
        axios.get(`${API}/produits`, { headers }),
      ]);
      setCfg(c.data);
      setRdvs(r.data);
      setParams(p.data);
      setProduits(prod.data || []);
      // Préconfigure Milan / Louis Ghost si vide
      const conf = c.data;
      if (!conf.produit_affiche_1_id || !conf.produit_affiche_2_id) {
        const findByModel = (label) => (prod.data || []).find((x) => (x.modele || "").toLowerCase().includes(label.toLowerCase()));
        const p1 = conf.produit_affiche_1_id || findByModel("milan")?.id || "";
        const p2 = conf.produit_affiche_2_id || findByModel("louis")?.id || findByModel("ghost")?.id || "";
        if ((p1 && p1 !== conf.produit_affiche_1_id) || (p2 && p2 !== conf.produit_affiche_2_id)) {
          setCfg({ ...conf, produit_affiche_1_id: p1, produit_affiche_2_id: p2 });
        }
      }
    } catch { toast.error("Erreur de chargement"); }
  };
  useEffect(() => { fetchAll(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const toggleJour = (n) => {
    const cur = cfg.jours_ouverts || [];
    setCfg({ ...cfg, jours_ouverts: cur.includes(n) ? cur.filter((x) => x !== n) : [...cur, n].sort() });
  };

  const saveConfig = async () => {
    try {
      await axios.put(`${API}/rdv/config`, cfg, { headers });
      toast.success("Réglages enregistrés");
    } catch { toast.error("Erreur d'enregistrement"); }
  };

  const setStatut = async (id, statut) => {
    try { await axios.put(`${API}/rdv/${id}`, { statut }, { headers }); fetchAll(); toast.success("Mis à jour"); }
    catch { toast.error("Erreur"); }
  };

  const remove = async (id) => {
    if (!window.confirm("Supprimer ce rendez-vous ?")) return;
    try { await axios.delete(`${API}/rdv/${id}`, { headers }); fetchAll(); toast.success("Supprimé"); }
    catch { toast.error("Erreur"); }
  };

  const copyLink = () => { navigator.clipboard.writeText(publicUrl); toast.success("Lien copié !"); };

  // Base URL pour SMS : utilise le lien global paramètres → sinon `public_base_url` RDV → sinon origine
  const smsBaseUrl = () => {
    if (params?.lien_rdv && /^https?:\/\//i.test(params.lien_rdv.trim())) {
      return params.lien_rdv.trim().replace(/\/[^/]*$/, "").replace(/\/prendre-rdv$/, "");
    }
    if (cfg?.public_base_url && cfg.public_base_url.trim()) return cfg.public_base_url.trim().replace(/\/$/, "");
    return window.location.origin;
  };
  const buildSmsLink = (typeLien) => {
    // Réutilise publicUrl (déjà résolu depuis params.lien_rdv) + suffixe ?type=
    const base = publicUrl.split("?")[0];
    return `${base}?type=${typeLien}`;
  };

  const openSmsDialog = (r) => {
    const prenom = ((r?.nom || "").split(" ")[0] || "").trim();
    // Type par défaut : "retrait" si numero_commande présent, sinon celui du RDV, sinon "visite"
    const typeLien = r?.numero_commande ? "retrait" : (r?.type_rdv === "retrait" ? "retrait" : "visite");
    const lien = buildSmsLink(typeLien);
    setSmsData({
      rdv_id: r?.id || "",
      telephone: r?.telephone || "",
      prenom,
      type_lien: typeLien,
      message: buildRdvSmsMessage(typeLien, prenom, lien),
    });
    setSmsError("");
    setSmsOpen(true);
  };

  // Regénère le message quand le type change dans le dialog
  const changeSmsType = (typeLien) => {
    const lien = buildSmsLink(typeLien);
    setSmsData((prev) => ({
      ...prev,
      type_lien: typeLien,
      message: buildRdvSmsMessage(typeLien, prev.prenom, lien),
    }));
  };

  const sendSms = async () => {
    if (smsSending) return; // anti double-clic
    if (!smsData.telephone) { toast.error("Téléphone manquant"); return; }
    setSmsSending(true);
    setSmsError("");
    // Log statut "pending" (best-effort)
    try {
      await axios.post(`${API}/rdv/${smsData.rdv_id || "none"}/sms-sent`, {
        type_lien: smsData.type_lien,
        statut: "pending",
        telephone: smsData.telephone,
        message: smsData.message,
      }, { headers });
    } catch { /* silent */ }

    try {
      // Ouvre l'app SMS native (système existant réutilisé)
      const tel = (smsData.telephone || "").replace(/[^0-9+]/g, "");
      const body = encodeURIComponent(smsData.message || "");
      window.location.href = `sms:${tel}?body=${body}`;
      // Log "sent" (best-effort)
      try {
        await axios.post(`${API}/rdv/${smsData.rdv_id || "none"}/sms-sent`, {
          type_lien: smsData.type_lien,
          statut: "sent",
          telephone: smsData.telephone,
          message: smsData.message,
        }, { headers });
      } catch { /* silent */ }
      toast.success("Le lien de rendez-vous a été envoyé par SMS.");
      setSmsOpen(false);
    } catch (err) {
      setSmsError("Le SMS n'a pas pu être envoyé.");
      try {
        await axios.post(`${API}/rdv/${smsData.rdv_id || "none"}/sms-sent`, {
          type_lien: smsData.type_lien,
          statut: "failed",
          telephone: smsData.telephone,
          message: smsData.message,
        }, { headers });
      } catch { /* silent */ }
    } finally {
      setSmsSending(false);
    }
  };

  // Rappels J-1 : RDV dans les 24h à venir (à confirmer)
  const now = new Date();
  const in48h = new Date(now.getTime() + 48 * 60 * 60 * 1000);
  const rdvsJ1 = rdvs.filter((r) => {
    if (r.statut === "annule") return false;
    try {
      const dt = new Date(`${r.date}T${r.heure || "00:00"}:00`);
      return dt > now && dt <= in48h;
    } catch { return false; }
  }).sort((a, b) => (a.date + a.heure).localeCompare(b.date + b.heure));

  const buildRappelMessage = (r) => {
    const prenom = (r.nom || "").split(" ")[0] || "client";
    const dateFmt = new Date(`${r.date}T${r.heure || "00:00"}`).toLocaleDateString('fr-FR', { weekday: 'long', day: 'numeric', month: 'long' });
    const adresse = cfg?.adresse || "notre dépôt";
    return `Bonjour ${prenom}, petit rappel de votre RDV chez TopCocoon ${dateFmt} à ${r.heure} au ${adresse}. Merci de nous prévenir en cas d'empêchement. À demain ! 🪑`;
  };

  const sendRappelSms = (r) => {
    const msg = encodeURIComponent(buildRappelMessage(r));
    const tel = (r.telephone || "").replace(/[^0-9+]/g, "");
    window.location.href = `sms:${tel}?body=${msg}`;
  };
  const sendRappelEmail = (r) => {
    if (!r.email) { toast.error("Pas d'email pour ce client"); return; }
    const subject = encodeURIComponent("Rappel de votre RDV TopCocoon");
    const body = encodeURIComponent(buildRappelMessage(r));
    window.location.href = `mailto:${r.email}?subject=${subject}&body=${body}`;
  };

  if (!cfg) return <div className="p-8 text-slate-400">Chargement…</div>;

  return (
    <div className="space-y-8" data-testid="rdv-admin-page">
      <div>
        <h1 className="text-3xl font-bold text-slate-800 flex items-center gap-2">
          <CalendarClock className="w-7 h-7 text-indigo-600" /> Rendez-vous (dépôt)
        </h1>
        <p className="text-slate-500 mt-1">Partagez le lien public pour que vos clients réservent un créneau au dépôt.</p>
      </div>

      <Card className="border-indigo-200 bg-indigo-50/40">
        <CardContent className="py-4 flex flex-col sm:flex-row sm:items-center gap-3">
          <Link2 className="w-5 h-5 text-indigo-600 shrink-0" />
          <code className="flex-1 text-sm text-indigo-800 break-all" data-testid="rdv-public-link">{publicUrl}</code>
          <Button size="sm" variant="outline" onClick={copyLink} data-testid="rdv-copy-link-btn"><Copy className="w-4 h-4 mr-1" />Copier</Button>
          <Button size="sm" variant="outline" onClick={() => window.open(publicUrl, "_blank")} data-testid="rdv-open-link-btn">Ouvrir</Button>
        </CardContent>
      </Card>

      <Card>
        <CardHeader><CardTitle className="text-lg">Réglages des créneaux</CardTitle></CardHeader>
        <CardContent className="space-y-5">
          <div className="flex items-center gap-3">
            <Switch checked={cfg.actif} onCheckedChange={(v) => setCfg({ ...cfg, actif: v })} data-testid="rdv-actif-switch" />
            <Label>Réservations activées</Label>
          </div>
          <div>
            <Label className="mb-2 block">Jours ouverts</Label>
            <div className="flex flex-wrap gap-2">
              {JOURS.map((j) => (
                <Button key={j.n} type="button" size="sm" variant={cfg.jours_ouverts?.includes(j.n) ? "default" : "outline"}
                  className={cfg.jours_ouverts?.includes(j.n) ? "bg-indigo-600" : ""}
                  onClick={() => toggleJour(j.n)} data-testid={`rdv-jour-${j.n}`}>{j.l}</Button>
              ))}
            </div>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="space-y-2"><Label>Heure début</Label><Input type="time" value={cfg.heure_debut} onChange={(e) => setCfg({ ...cfg, heure_debut: e.target.value })} data-testid="rdv-debut-input" /></div>
            <div className="space-y-2"><Label>Heure fin</Label><Input type="time" value={cfg.heure_fin} onChange={(e) => setCfg({ ...cfg, heure_fin: e.target.value })} data-testid="rdv-fin-input" /></div>
            <div className="space-y-2"><Label>Durée créneau (min)</Label><Input type="number" min="5" step="5" value={cfg.duree_creneau} onChange={(e) => setCfg({ ...cfg, duree_creneau: parseInt(e.target.value) || 30 })} data-testid="rdv-duree-input" /></div>
            <div className="space-y-2"><Label>Max / créneau</Label><Input type="number" min="1" value={cfg.max_par_creneau} onChange={(e) => setCfg({ ...cfg, max_par_creneau: parseInt(e.target.value) || 1 })} data-testid="rdv-max-input" /></div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2"><Label>Adresse du dépôt</Label><Input value={cfg.adresse || ""} onChange={(e) => setCfg({ ...cfg, adresse: e.target.value })} placeholder="Rue, code postal, ville" data-testid="rdv-adresse-input" /></div>
            <div className="space-y-2"><Label>Téléphone showroom</Label><Input value={cfg.telephone || ""} onChange={(e) => setCfg({ ...cfg, telephone: e.target.value })} placeholder="Ex: 0470 37 57 35" data-testid="rdv-telephone-input" /></div>
          </div>
          <div className="space-y-2">
            <Label>URL publique de production (utilisée dans les SMS)</Label>
            <Input value={cfg.public_base_url || ""} onChange={(e) => setCfg({ ...cfg, public_base_url: e.target.value })} placeholder="Ex: https://www.top-cocoon.be" data-testid="rdv-public-base-url-input" />
            <p className="text-xs text-slate-500">Laisse vide pour utiliser l&apos;URL courante. Sinon renseigne le domaine de production (jamais le lien Preview).</p>
          </div>
          <div className="space-y-2"><Label>Message d&apos;accueil</Label><Textarea value={cfg.message || ""} onChange={(e) => setCfg({ ...cfg, message: e.target.value })} data-testid="rdv-message-input" /></div>

          {/* Produits affichés sur la page publique de RDV */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 pt-2 border-t">
            <div className="space-y-2">
              <Label>Produit affiché 1</Label>
              <Select value={cfg.produit_affiche_1_id || ""} onValueChange={(v) => setCfg({ ...cfg, produit_affiche_1_id: v })}>
                <SelectTrigger data-testid="rdv-produit1-select"><SelectValue placeholder="Choisir un produit…" /></SelectTrigger>
                <SelectContent>
                  {produits.map((p) => (
                    <SelectItem key={p.id} value={p.id}>{p.modele} — {p.couleur}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Produit affiché 2</Label>
              <Select value={cfg.produit_affiche_2_id || ""} onValueChange={(v) => setCfg({ ...cfg, produit_affiche_2_id: v })}>
                <SelectTrigger data-testid="rdv-produit2-select"><SelectValue placeholder="Choisir un produit…" /></SelectTrigger>
                <SelectContent>
                  {produits.map((p) => (
                    <SelectItem key={p.id} value={p.id}>{p.modele} — {p.couleur}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          <Button onClick={saveConfig} className="bg-indigo-600 hover:bg-indigo-700" data-testid="rdv-save-config-btn"><Save className="w-4 h-4 mr-2" />Enregistrer</Button>
        </CardContent>
      </Card>

      {/* Rappels J-1 : RDV dans les 24-48h (toujours visible) */}
      <Card className="border-amber-300 bg-amber-50/40" data-testid="rappels-j1-card">
        <CardHeader>
          <CardTitle className="text-lg flex items-center gap-2 text-amber-800">
            <Bell className="w-5 h-5" /> Rappels à envoyer ({rdvsJ1.length})
          </CardTitle>
          <p className="text-sm text-slate-600 mt-1">RDV dans les prochaines 24-48h — envoie un rappel manuel</p>
        </CardHeader>
        <CardContent>
          {rdvsJ1.length === 0 ? (
            <p className="text-sm text-slate-400 py-3">Aucun RDV à rappeler dans les 24-48h à venir 😌</p>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Quand</TableHead><TableHead>Client</TableHead><TableHead className="text-right">Rappel</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {rdvsJ1.map((r) => (
                  <TableRow key={`j1-${r.id}`} data-testid={`rappel-j1-${r.id}`}>
                    <TableCell className="font-medium">{r.date} <span className="text-amber-700">{r.heure}</span></TableCell>
                    <TableCell><div>{r.nom}</div><div className="text-xs text-slate-500">{r.telephone}{r.email ? ` · ${r.email}` : ""}</div></TableCell>
                    <TableCell className="text-right space-x-1">
                      {r.telephone && (
                        <Button size="sm" className="bg-green-600 hover:bg-green-700 text-white" onClick={() => sendRappelSms(r)} title="Envoyer SMS de rappel" data-testid={`rappel-sms-${r.id}`}>
                          <MessageCircle className="w-4 h-4 mr-1" />SMS
                        </Button>
                      )}
                      {r.email && (
                        <Button size="sm" variant="outline" onClick={() => sendRappelEmail(r)} title="Envoyer email de rappel" data-testid={`rappel-email-${r.id}`}>
                          <Mail className="w-4 h-4 mr-1" />Email
                        </Button>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader><CardTitle className="text-lg">Demandes de rendez-vous</CardTitle></CardHeader>
        <CardContent>
          {/* Bouton global : envoyer le lien à un numéro libre (sans RDV) */}
          <div className="mb-3 flex justify-end">
            <Button size="sm" variant="outline" className="border-indigo-300 text-indigo-700 hover:bg-indigo-50" onClick={() => openSmsDialog(null)} data-testid="rdv-send-link-btn-global">
              <Send className="w-4 h-4 mr-1" />Envoyer le lien par SMS
            </Button>
          </div>
          {rdvs.length === 0 ? (
            <p className="text-slate-400 text-sm">Aucun rendez-vous pour l&apos;instant.</p>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Date / Heure</TableHead><TableHead>Type</TableHead><TableHead>Client</TableHead><TableHead>Commentaire</TableHead><TableHead>Statut</TableHead><TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {rdvs.map((r) => (
                  <TableRow key={r.id} data-testid={`rdv-row-${r.id}`}>
                    <TableCell className="font-medium">{r.date} <span className="text-indigo-600">{r.heure}</span></TableCell>
                    <TableCell>
                      {r.type_rdv === "retrait" ? (
                        <Badge className="bg-emerald-100 text-emerald-800 border-0">Retrait</Badge>
                      ) : r.type_rdv === "visite" ? (
                        <Badge className="bg-indigo-100 text-indigo-800 border-0">Visite</Badge>
                      ) : (
                        <span className="text-xs text-slate-400">—</span>
                      )}
                    </TableCell>
                    <TableCell><div>{r.nom}</div><div className="text-xs text-slate-500">{r.telephone}{r.email ? ` · ${r.email}` : ""}</div></TableCell>
                    <TableCell className="text-sm text-slate-600 max-w-[220px] truncate">{r.commentaire}</TableCell>
                    <TableCell><Badge className={`${STATUT_COLORS[r.statut] || ""} border-0`}>{STATUT_LABELS[r.statut] || r.statut}</Badge></TableCell>
                    <TableCell className="text-right space-x-1">
                      {r.telephone && (
                        <Button size="sm" variant="outline" className="text-indigo-700 border-indigo-300 hover:bg-indigo-50" onClick={() => openSmsDialog(r)} title="Envoyer le lien par SMS" data-testid={`rdv-send-link-btn-${r.id}`}>
                          <Send className="w-4 h-4" />
                        </Button>
                      )}
                      {r.statut !== "confirme" && <Button size="sm" variant="outline" className="text-green-600" onClick={() => setStatut(r.id, "confirme")} title="Confirmer" data-testid={`rdv-confirm-${r.id}`}><Check className="w-4 h-4" /></Button>}
                      {r.statut !== "annule" && <Button size="sm" variant="outline" onClick={() => setStatut(r.id, "annule")} title="Annuler" data-testid={`rdv-cancel-${r.id}`}>Annuler</Button>}
                      <Button size="sm" variant="outline" className="text-red-600 hover:bg-red-50" onClick={() => remove(r.id)} data-testid={`rdv-delete-${r.id}`}><Trash2 className="w-4 h-4" /></Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Dialog : Envoyer le lien par SMS */}
      <Dialog open={smsOpen} onOpenChange={(v) => { if (!smsSending) setSmsOpen(v); }}>
        <DialogContent className="max-w-md mx-4" data-testid="rdv-sms-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Send className="w-5 h-5 text-indigo-600" />
              Envoyer le lien par SMS
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-2">
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label className="text-sm">Téléphone</Label>
                <Input value={smsData.telephone} onChange={(e) => setSmsData({ ...smsData, telephone: e.target.value })} placeholder="+32…" data-testid="rdv-sms-tel-input" />
              </div>
              <div className="space-y-1.5">
                <Label className="text-sm">Prénom (facultatif)</Label>
                <Input value={smsData.prenom} onChange={(e) => {
                  const p = e.target.value;
                  const lien = buildSmsLink(smsData.type_lien);
                  setSmsData({ ...smsData, prenom: p, message: buildRdvSmsMessage(smsData.type_lien, p.trim(), lien) });
                }} data-testid="rdv-sms-prenom-input" />
              </div>
            </div>

            <div className="space-y-1.5">
              <Label className="text-sm">Type de lien</Label>
              <Select value={smsData.type_lien} onValueChange={changeSmsType}>
                <SelectTrigger data-testid="rdv-sms-type-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="visite">Visite du showroom</SelectItem>
                  <SelectItem value="retrait">Retrait d&apos;une commande</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-1.5">
              <Label className="text-sm">Message SMS (modifiable)</Label>
              <Textarea rows={6} value={smsData.message} onChange={(e) => setSmsData({ ...smsData, message: e.target.value })} className="text-sm" data-testid="rdv-sms-message-input" />
              <p className="text-[11px] text-slate-500 break-all">Lien inséré : {buildSmsLink(smsData.type_lien)}</p>
            </div>

            {smsError && (
              <div className="p-2 rounded-lg bg-red-50 border border-red-200 text-sm text-red-800 flex items-center justify-between" data-testid="rdv-sms-error">
                <span>{smsError}</span>
                <Button size="sm" variant="outline" onClick={sendSms} disabled={smsSending} data-testid="rdv-sms-retry-btn">
                  Réessayer
                </Button>
              </div>
            )}

            <div className="flex gap-2 pt-2">
              <Button variant="outline" className="flex-1" onClick={() => setSmsOpen(false)} disabled={smsSending}>Annuler</Button>
              <Button
                onClick={sendSms}
                disabled={smsSending || !smsData.telephone}
                className="flex-1 bg-indigo-600 hover:bg-indigo-700"
                data-testid="rdv-sms-send-btn"
              >
                {smsSending ? "Envoi en cours…" : (<><Send className="w-4 h-4 mr-2" />Envoyer le SMS</>)}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default RendezVous;
